import React, { useEffect, useMemo } from 'react';

import { getOlympsConfig } from 'platform-components/src/components/app-factory/selectors';
import { getCertificateSuggestions } from 'platform-components/src/components/user/selectors';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { downloadCSV, tableToCSV } from 'platform-components/utils';
import { useIntl } from 'react-intl';
import throttle from 'lodash/throttle';

import { getLessonSlug } from 'common.components/lesson/selectors';
import { SpecCourseLessonViewSectionContent } from 'common.components/spec-course-lesson-view/__section-content/spec-course-lesson-view__section-content';
import { SpecCourseLessonViewSectionTitle } from 'common.components/spec-course-lesson-view/__section-title/spec-course-lesson-view__section-title';
import { SpecCourseLessonViewSection } from 'common.components/spec-course-lesson-view/__section/spec-course-lesson-view__section';

import { OlympsResults } from '@edu-frontend/modules/src/features/olympsResults';
import { ThemeProvider } from '@edu-frontend/modules/src/shared/lib/ThemeProvider';
import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';
import ym from 'react-yandex-metrika';
import { RoverBoardgameBanner } from '../__rover-boardgame-banner/results__rover-boardgame-banner';
import { StudentsAwardsBanner } from '../__students-awards-banner/results__students-awards-banner';
import { StudentsCertificatesBanner } from '../__students-certificates-banner/results__students-certificates-banner';
import { SpecCourseLessonViewResultsTable } from '../__table/results__table';
import { TeacherActiveParticipantCertBanner } from '../__teacher-active-participant-cert-banner/results__teacher-active-participant-cert-banner';
import { TeacherCertificateBanner } from '../__teacher-certificate-banner/results__teacher-certificate-banner';
import { getSpecCourseLessonMode } from '../../spec-course.lesson-view/selectors';
import { MODES_TO_SHOW_RESULTS } from '../../spec-course.store/constants';
import {
    getShouldShowStudentsAwardsBanner,
    getShouldShowStudentsCertificatesBanner,
    getShouldShowTeacherCertificateBanner,
} from '../selectors';
import { getResultsDescriptionId } from '../utils/getResultsDescriptionId';
import { useSpecialLessonAwards } from '../queries';
import { sanitizeCsvValue } from '../utils/sanitizeCsvValue';

import './results__lesson-results.scss';

interface Props {
    awardsRef: React.RefObject<HTMLDivElement>;
    resultsRef: React.RefObject<HTMLDivElement>;
}

interface Params {
    classId: string;
    courseId: string;
}

export const SpecCourseLessonResults = (props: Props) => {
    /* eslint complexity: 0 */
    const { resultsRef } = props;
    const { classId, courseId: strCourseId } = useParams<Params>();
    const courseId = Number(strCourseId);
    const intl = useIntl();

    const mode = useSelector((store) => getSpecCourseLessonMode(store, { classId }));
    const resultsDescriptionId = useMemo(() => getResultsDescriptionId(mode), [mode]);
    const certificatesSuggestions = useSelector(getCertificateSuggestions);
    const shouldShowTeacherCertificateBanner = useSelector(getShouldShowTeacherCertificateBanner);
    const shouldShowStudentCertificatesBanner = useSelector((storeState) =>
        getShouldShowStudentsCertificatesBanner(storeState, { courseId })
    );
    const shouldShowCertificatesSection =
        MODES_TO_SHOW_RESULTS.includes(mode) &&
        (shouldShowTeacherCertificateBanner || shouldShowStudentCertificatesBanner);

    const slug = useSelector(getLessonSlug);

    const olympsConfig = useSelector(getOlympsConfig);
    const olympConfig = olympsConfig.olymps.find((olymp) => slug === olymp.slug);
    const activeParticipationCertSlug = olympConfig?.teacher?.results?.banners?.activeParticipationCertSlug;

    const shouldShowRoverBoardgameBanner = olympConfig?.teacher?.results?.banners?.roverBoardgame || false;
    const shouldShowStudentsAwardsBanner = useSelector((storeState) =>
        getShouldShowStudentsAwardsBanner(storeState, { courseId })
    );

    const shouldShowActiveParticipationCertBanner =
        activeParticipationCertSlug !== undefined && certificatesSuggestions.includes(activeParticipationCertSlug);

    const banners = olympConfig?.teacher?.results?.banners?.teacherDownloadResultAndTeachLessonBanners;

    const { data: specialLessonAwards } = useSpecialLessonAwards(slug, { enabled: !!banners });

    const downloadClick = useMemo(() => {
        const studentsData = specialLessonAwards?.studentsData ?? [];
        const headers = [
            intl.formatMessage({ id: `specialLessons.${slug}.results.csvHeaders.student`, defaultMessage: 'Ученик' }),
            intl.formatMessage({ id: `specialLessons.${slug}.results.csvHeaders.teamName`, defaultMessage: 'Класс' }),
            intl.formatMessage({ id: `specialLessons.${slug}.results.csvHeaders.points`, defaultMessage: 'Баллы' }),
            intl.formatMessage({
                id: `specialLessons.${slug}.results.csvHeaders.maxPoints`,
                defaultMessage: 'Максимальные баллы',
            }),
            intl.formatMessage({
                id: `specialLessons.${slug}.results.csvHeaders.awardStatus`,
                defaultMessage: 'Статус',
            }),
        ];
        const content = studentsData.map(({ awardStatus, firstName, lastName, maxPoints, points, teamName }) =>
            [
                `${lastName} ${firstName}`,
                `${teamName}`,
                `${points}`,
                `${maxPoints}`,
                `${intl.formatMessage({
                    id: `specialLesson.results.csvContent.status.${awardStatus}`,
                    defaultMessage: awardStatus,
                })}`,
            ].map(sanitizeCsvValue)
        );
        const specialLessonAwardsTableCsv = tableToCSV([headers, ...content]);
        const throttleTime = parseInt(intl.formatMessage({ id: 'specialLessons.results.csvThrottleTime' }), 10) || 1000;
        const downloadButtonConfig = banners?.bannerLeft;
        return throttle(() => {
            ym('reachGoal', downloadButtonConfig?.metrikaGoal);

            const fileName = intl.formatMessage({
                id: `specialLessons.${slug}.results.csvName`,
                defaultMessage: 'results.csv',
            });
            downloadCSV(specialLessonAwardsTableCsv, fileName, true);
        }, throttleTime);
    }, [specialLessonAwards, intl, slug, banners?.bannerLeft]);

    // отменяем отложенное выполнение функции при анмаунте
    useEffect(() => () => downloadClick.cancel(), [downloadClick]);

    const { downloadButton, teachLessonButton } = useMemo(() => {
        const downloadButtonConfig = banners?.bannerLeft;
        const teachLessonButtonConfig = banners?.bannerRight;

        const currentDate = Date.now();
        const isAvailableDownloadDateEnd =
            !downloadButtonConfig?.endDate || currentDate < new Date(downloadButtonConfig?.endDate).getTime();
        const isAvailableDownloadDateStart =
            !downloadButtonConfig?.startDate || currentDate > new Date(downloadButtonConfig?.startDate).getTime();

        const isAvailableTeachDateEnd =
            !teachLessonButtonConfig?.endDate || currentDate < new Date(teachLessonButtonConfig?.endDate).getTime();
        const isAvailableTeachDateStart =
            !teachLessonButtonConfig?.startDate || currentDate > new Date(teachLessonButtonConfig?.startDate).getTime();

        return {
            downloadButton: {
                ...downloadButtonConfig,
                enabled: downloadButtonConfig?.enabled && isAvailableDownloadDateEnd && isAvailableDownloadDateStart,
                onClick: downloadClick,
            },
            teachLessonButton: {
                ...teachLessonButtonConfig,
                onClick: () => {
                    ym('reachGoal', teachLessonButtonConfig?.metrikaGoal);
                    window.open(teachLessonButtonConfig?.redirectUrl, '_blank');
                },
                enabled: teachLessonButtonConfig?.enabled && isAvailableTeachDateEnd && isAvailableTeachDateStart,
            },
        };
    }, [banners, downloadClick]);

    return (
        <div ref={resultsRef}>
            {shouldShowCertificatesSection && (
                <>
                    <SpecCourseLessonViewSection mix="spec-course-results__certificates-section">
                        <SpecCourseLessonViewSectionTitle id="specCourse.lessonView.certificates.title" />
                        <SpecCourseLessonViewSectionContent mix="spec-course-results__certificates-wrapper">
                            {shouldShowTeacherCertificateBanner && (
                                <TeacherCertificateBanner olympConfig={olympConfig} />
                            )}
                            {shouldShowActiveParticipationCertBanner && (
                                <TeacherActiveParticipantCertBanner olympConfig={olympConfig} />
                            )}
                        </SpecCourseLessonViewSectionContent>
                    </SpecCourseLessonViewSection>
                    {shouldShowStudentCertificatesBanner && (
                        <SpecCourseLessonViewSection mix="spec-course-results__certificates-section">
                            <SpecCourseLessonViewSectionTitle id="specCourse.lessonView.certificates.studentAwardsTitle" />
                            <SpecCourseLessonViewSectionContent mix="spec-course-results__certificates-wrapper">
                                {shouldShowRoverBoardgameBanner && <RoverBoardgameBanner />}
                                <StudentsCertificatesBanner olympConfig={olympConfig} />
                            </SpecCourseLessonViewSectionContent>
                            <SpecCourseLessonViewSectionContent mix="spec-course-results__certificates-wrapper">
                                {shouldShowStudentsAwardsBanner && <StudentsAwardsBanner eventSlug={slug} />}
                            </SpecCourseLessonViewSectionContent>
                        </SpecCourseLessonViewSection>
                    )}
                </>
            )}
            {banners && (
                <div className="spec-course-results__banners">
                    <ThemeProvider isRoot={false} theme={hrLight}>
                        <OlympsResults
                            description={banners.description || ''}
                            downloadButton={downloadButton}
                            header={banners.header || ''}
                            teachLessonButton={teachLessonButton}
                        />
                    </ThemeProvider>
                </div>
            )}
            <SpecCourseLessonViewResultsTable descriptionId={resultsDescriptionId} />
        </div>
    );
};
